import { parseAsStringLiteral, useQueryState } from "@msuite/ui/libs/nuqs";
import { useQuery } from "@msuite/ui/query";
import { createRowActions, DataTable } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import type { GetReturnOrdersQuerySchema } from "@msuite/utils/schemas";
import { RETURN_ORDER_STATES } from "@msuite/utils/stock";
import { client } from "@/hono";
import { returnOrdersTableColumns } from "../return-orders-table-config";
import { ReturnOrdersMenu } from "./return-orders-menu";
import { RowActions } from "./row-actions";

/** Props Interface */
interface ReturnOrdersTableProps {
	query?: GetReturnOrdersQuerySchema;
	persistenceKey?: `${string}-table`;
}

export function ReturnOrdersTable({
	query,
	persistenceKey = "return-orders-table",
}: ReturnOrdersTableProps) {
	/**
	 * State
	 *
	 * The filter lives in the URL rather than under `persistenceKey`, the same split the order
	 * table makes: that key persists the table's own layout — column order, widths, pinning — and
	 * a filter that narrows what the server returns is not layout. In the URL it is a link
	 * somebody can send, and it is gone on the next visit rather than quietly narrowing it.
	 *
	 * It opens on `all`, which is where this list differs from the order list deliberately. That
	 * one defaults to `pending` because it is long and the freshly raised orders are the work; a
	 * warehouse runs a handful of return runs at a time, so hiding the settled ones buys nothing
	 * and a run vanishing from view the moment its first booking lands reads as a bug.
	 */
	const [state, setState] = useQueryState(
		"return-order-state",
		parseAsStringLiteral(["all", ...RETURN_ORDER_STATES] as const).withDefault("all"),
	);

	/** Computed */
	const fullQuery: GetReturnOrdersQuerySchema = {
		...query,
		state: state === "all" ? undefined : [state],
	};

	/** Query */
	const { data, isLoading, refetch, queryKey } = useQuery({
		queryKey: ["return-orders", JSON.stringify(fullQuery)],
		queryFn: async () => {
			const response = await client.stock["return-orders"].$get({ query: fullQuery });
			const data = await responseHandler(response);
			return data.returnOrders;
		},
	});

	/** Render */
	return (
		<DataTable
			data={data}
			columns={[...returnOrdersTableColumns, createRowActions(RowActions)]}
			queryKey={queryKey}
			isLoading={isLoading}
			refetch={refetch}
			persistenceKey={persistenceKey}
			// The state is what the list is read for and sits behind six columns, so it starts
			// pinned — the same default the order table takes.
			initialPinningState={{ end: ["state"] }}
			menu={
				<ReturnOrdersMenu
					onComplete={refetch}
					state={state}
					onStateChange={setState}
				/>
			}
		/>
	);
}
